import {
    ChangeDetectorRef,
    Directive,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges
} from "@angular/core";
import { IRowState } from "../interfaces/row-state";
import { RowStateType } from "../enums/row-state-type";
import { MatTooltip } from "@angular/material/tooltip";
import { StringUtil } from "@shopliftr/common-js/data";
import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { LIST_STATE_SERVICE } from "../constants/tokens";
import {
    Subject, takeUntil
} from "rxjs";
import { ProcessingDirective } from "../../application/directives/processing/processing.directive";
import { DisabledDirective } from "../../application/directives/disabled/disabled.directive";
import { ListUtil } from "../util/list-util";

/**
 * Directive that manages the state and behavior of table/list rows.
 *
 * This directive provides:
 * - Row state management (error, warning, disabled, highlighted states)
 * - Tooltip integration for state messages
 * - Automatic state-based styling
 * - Row visibility detection
 * - Scrolling utilities
 *
 * Features:
 * - Automatically applies appropriate CSS classes based on row state
 * - Integrates with MatTooltip for state messages
 * - Supports clickable/non-clickable states
 * - Provides methods to check visibility and scroll into view
 *
 * Usage:
 * ```html
 * <mat-row *matRowDef="let row; columns: displayedColumns;" mgdRow
 *     [row]="row"
 *     [clickable]="true" (click)="onRowClick()">
 *     <!-- row content -->
 * </mat-row>
 * ```
 */
@Directive({
    selector: "[mgdRow]",
    hostDirectives: [
        MatTooltip,
        {
            directive: DisabledDirective,
            inputs: ["disabled"]
        },
        {
            directive: ProcessingDirective,
            inputs: ["disableWhileProcessing"]
        }
    ],
    host: {

        "[class.mgd-list-row]": "true"
    }
})
export class RowDirective implements OnChanges, OnInit, OnDestroy {

    /** The row data object that this directive manages */
    @Input() row!: object;

    /**
     * Whether the row should be clickable. Defaults to true.
     * Transformed to boolean using coerceBooleanProperty
     */
    @Input({
        alias: "clickable",
        transform: coerceBooleanProperty
    }) clickableInput?: boolean = true;

    /** Whether the row is currently clickable based on state and input */
    @HostBinding("class.mgd-row-clickable")
    clickable = false;

    /** Whether the row is currently highlighted */
    @HostBinding("class.mgd-row-highlighted")
    highlighted = false;

    /** Whether the row is currently disabled */
    @HostBinding("class.mgd-row-disabled")
    disabled = false;

    /** Whether the row is in an error state */
    @HostBinding("class.mgd-row-error")
    error = false;

    /** Whether the row is in a warning state */
    @HostBinding("class.mgd-row-warning")
    warning = false;

    /** Current state of the row */
    rowState?: IRowState;

    /** Subject to handle row changes and cleanup subscriptions */
    private readonly _rowChange$ = new Subject<void>();

    /** Optional service to manage list state */
    private readonly _listState? = inject(LIST_STATE_SERVICE, { optional: true });

    /** Material tooltip for displaying state messages */
    private readonly _matTooltip = inject(MatTooltip);

    /** Reference to the host element */
    private readonly _elementRef: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);

    /** Directive to handle processing state */
    private readonly _processingDirective = inject(ProcessingDirective, { self: true });

    /** Reference to Angular's change detector */
    private readonly _cdRef = inject(ChangeDetectorRef);

    /**
     * Returns true if this row is partially visible in the viewport
     */
    get isInViewport(): boolean {

        const bounds = this._elementRef.nativeElement.getBoundingClientRect();
        return ((bounds.top > 0 && bounds.top < window.innerHeight)
            || (bounds.bottom > 0 && bounds.bottom < window.innerHeight))
        && ((bounds.left > 0 && bounds.left < window.innerWidth) || (bounds.right > 0 && bounds.right < window.innerWidth));
    }

    /**
     * Displays the rowState.message as a mat tooltip for
     * highlighted, disabled and tooltip row states
     *
     * Error and warning state messages are displayed using the RowStateInfoComponent
     */
    @HostListener("mouseover") mouseover(): void {

        if (!StringUtil.emptyString(this.rowState?.message)
            && [RowStateType.Disabled, RowStateType.Tooltip, RowStateType.Highlighted].includes(this.rowState?.type)) {
            this._matTooltip.message = this.rowState.message;
            this._matTooltip.show();
        }
    }

    /**
     * Hides the tooltip
     */
    @HostListener("mouseout") mouseout(): void {

        this._matTooltip.hide();
    }

    /**
     * Angular lifecycle hook that applies the initial clickable state, so a row without a list state service
     * still honours the `clickable` input (which defaults to true).
     */
    ngOnInit(): void {
        this._updateClickable();
    }

    /**
     * Angular lifecycle hook that handles cleanup when the directive is destroyed.
     * Completes the row change subject to prevent memory leaks.
     */
    ngOnDestroy(): void {
        this._rowChange$.next();
        this._rowChange$.complete();
    }

    /**
     * Angular lifecycle hook that responds to input changes.
     * When the row input changes, it triggers a row change event and updates the row state.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.row) {
            this._rowChange$.next();
            this._setupRowState();
        }
        if (changes.clickableInput) {
            this._updateClickable();
            this._cdRef.markForCheck();
        }
    }

    /**
     * Scroll the table row into view.
     *
     * If options not provided,
     * It will scroll to the element in the vertical center, and horizontally nearest position
     * @param options Optional ScrollIntoViewOptions to customize scrolling behavior
     */
    scrollIntoView(options?: ScrollIntoViewOptions): void {
        options = options ?? {
            block: "center",
            inline: "nearest"
        };
        this._elementRef.nativeElement.scrollIntoView(options);
    }

    /**
     * Sets up the row state subscription and initial state.
     * Subscribes to state changes from the list state service if available.
     * @private
     */
    private _setupRowState(): void {
        if (this.row && this._listState) {
            const rawRow = ListUtil.unwrapRow<object>(this.row);
            this._updateRowState(this._listState.getRowState(rawRow));
            this._listState.getRowState$(rawRow)
                .pipe(
                    takeUntil(this._rowChange$),
                )
                .subscribe((state: IRowState) => this._updateRowState(state));
        }
    }

    /**
     * Updates the row's state and all related binding properties.
     * Triggers change detection after updating state.
     * @param state The new row state to apply
     * @private
     */
    private _updateRowState(state: IRowState): void {
        this.rowState = state;
        this.highlighted = this.rowState?.type === RowStateType.Highlighted;
        this.disabled = this.rowState?.type === RowStateType.Disabled || this._processingDirective.disabled;
        this.error = this.rowState?.type === RowStateType.Error;
        this.warning = this.rowState?.type === RowStateType.Warning;
        this._updateClickable();
        this._cdRef.markForCheck();
    }

    /**
     * Update the clickable state based on the row's state and input
     * @private
     */
    private _updateClickable(): void {
        this.clickable = this.clickableInput && !this.disabled;
    }
}
